/**
 * Registro data-driven de ESPECIES DE PEZ (skill_fishing).
 *
 * Fuente única de verdad de los peces: la consumen `resourceNodes.ts` (para armar los tipos
 * de nodo `fish_node_<species>` = cardúmenes) e `items.ts` (para generar los pescados
 * `material_fish_<species>` y las cañas `tool_fishing_rod_<species>`). Vive en su propio
 * módulo para evitar el import circular items ↔ resourceNodes (sólo importa el TIPO ToolTier,
 * que se borra en runtime).
 *
 * Escalones espejando las 7 especies de árbol (niveles 1/15/30/45/60/75/90; capacidad
 * invertida: el fácil rinde más). Cada pez exige su CAÑA puntual (1 por pez), crafteada en
 * Carpintería con la madera 1:1 de la especie de árbol del mismo escalón.
 *
 * NOTA (futuro): a pedido del usuario, cada pez tendrá su propio TIPO de herramienta
 * (caña/arpón/caja/red), no todas cañas, con recetas que pedirán items extra. La arquitectura
 * ya lo soporta (gate por item puntual + recetas con múltiples inputs); por ahora todas cañas.
 *
 * Ver docs/FISHING_SKILL.md.
 */

import type { ToolTier } from './items';

export interface FishSpeciesDef {
  /** ID inglés (sardine, trout, salmon, dorado, croaker, grouper, tuna). */
  species: string;
  /** Nombre visible del pez (español). */
  label: string;
  /** Nivel de Pesca requerido para pescarlo. */
  fishLevel: number;
  /** Tier del escalón (consistencia/wiki; el gate real es la caña). Espeja el árbol del escalón. */
  tier: ToolTier;
  /** Madera 1:1 con la que se craftea su caña (material_wood_<especie-de-árbol>). */
  wood: string;
  /** XP de Pesca por unidad pescada. */
  xp: number;
  /** Respawn del cardumen (ms). */
  respawnMs: number;
  /** Peces por cardumen (medium; small/large derivan). Invertido: sardina 17 → atún 5. */
  capacity: number;
}

export const FISH_SPECIES: FishSpeciesDef[] = [
  { species: 'sardine', label: 'Sardina', fishLevel: 1, tier: 'iron', wood: 'material_wood_maple', xp: 25, respawnMs: 4000, capacity: 17 },
  { species: 'trout', label: 'Trucha', fishLevel: 15, tier: 'iron', wood: 'material_wood_willow', xp: 45, respawnMs: 7000, capacity: 15 },
  { species: 'salmon', label: 'Salmón', fishLevel: 30, tier: 'iron', wood: 'material_wood_oak', xp: 65, respawnMs: 11000, capacity: 13 },
  { species: 'dorado', label: 'Dorado', fishLevel: 45, tier: 'steel', wood: 'material_wood_birch', xp: 85, respawnMs: 22500, capacity: 11 },
  { species: 'croaker', label: 'Corvina', fishLevel: 60, tier: 'mithril', wood: 'material_wood_palm', xp: 110, respawnMs: 45000, capacity: 9 },
  { species: 'grouper', label: 'Mero', fishLevel: 75, tier: 'adamant', wood: 'material_wood_jacaranda', xp: 135, respawnMs: 75000, capacity: 7 },
  { species: 'tuna', label: 'Atún', fishLevel: 90, tier: 'rune', wood: 'material_wood_pine', xp: 160, respawnMs: 90000, capacity: 5 },
];

/** ID de la caña (herramienta de pesca) de una especie. */
export function rodIdForFish(species: string): string {
  return `tool_fishing_rod_${species}`;
}

/** ID del ítem de pescado de una especie. */
export function fishItemId(species: string): string {
  return `material_fish_${species}`;
}
